export const ANALYTICS_MAX_PENDING_EVENTS = 100;
export const DEFAULT_MIXPANEL_API_URL = "https://api.mixpanel.com";
export const MIXPANEL_LOCAL_COLLECTOR_TOKEN = "local-collector";

export const E_MixpanelDeliveryMode = {
  Native: "native",
  JavaScript: "js",
  DirectHttp: "direct_http",
} as const;

export type T_MixpanelDeliveryMode =
  (typeof E_MixpanelDeliveryMode)[keyof typeof E_MixpanelDeliveryMode];

export type T_AnalyticsProperty = boolean | null | number | string | string[];

export type T_AnalyticsProperties = Record<
  string,
  T_AnalyticsProperty | undefined
>;

export type T_MixpanelTrackEvent = {
  event: string;
  properties: Record<string, T_AnalyticsProperty>;
};

type T_MixpanelEventPropertiesInput = {
  clientTransport: string;
  eventProperties?: T_AnalyticsProperties;
  superProperties?: T_AnalyticsProperties;
  userId: string | null;
  userProperties?: T_AnalyticsProperties;
};

type T_MixpanelTrackPayloadInput = T_MixpanelEventPropertiesInput & {
  anonymousId: string;
  eventName: string;
  insertId: string;
  token: string;
};

export function parseMixpanelDeliveryMode(
  value: string | undefined,
): T_MixpanelDeliveryMode | null {
  const normalized = value?.trim().toLowerCase();

  switch (normalized) {
    case E_MixpanelDeliveryMode.Native:
      return E_MixpanelDeliveryMode.Native;
    case E_MixpanelDeliveryMode.JavaScript:
    case "javascript":
    case "sdk":
      return E_MixpanelDeliveryMode.JavaScript;
    case E_MixpanelDeliveryMode.DirectHttp:
    case "direct-http":
    case "http":
      return E_MixpanelDeliveryMode.DirectHttp;
    default:
      return null;
  }
}

export function normalizeMixpanelApiUrl(value?: string): string {
  const trimmed = value?.trim() || DEFAULT_MIXPANEL_API_URL;
  return trimmed.replace(/\/+$/, "");
}

export function getMixpanelTrackUrl(apiUrl?: string): string {
  return `${normalizeMixpanelApiUrl(apiUrl)}/track?ip=1`;
}

export function resolveMixpanelToken(
  token: string | undefined,
  deliveryMode: T_MixpanelDeliveryMode,
): string | null {
  const trimmed = token?.trim();
  if (trimmed) return trimmed;

  return deliveryMode === E_MixpanelDeliveryMode.DirectHttp
    ? MIXPANEL_LOCAL_COLLECTOR_TOKEN
    : null;
}

export function cleanAnalyticsProperties(
  properties?: T_AnalyticsProperties,
): Record<string, T_AnalyticsProperty> {
  const cleaned: Record<string, T_AnalyticsProperty> = {};

  if (!properties) return cleaned;

  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }

  return cleaned;
}

export function getMixpanelEventProperties({
  clientTransport,
  eventProperties,
  superProperties,
  userId,
  userProperties,
}: T_MixpanelEventPropertiesInput): Record<string, T_AnalyticsProperty> {
  return {
    ...cleanAnalyticsProperties(superProperties),
    ...cleanAnalyticsProperties(userProperties),
    ...cleanAnalyticsProperties(eventProperties),
    user_id: userId,
    client_transport: clientTransport,
  };
}

export function buildMixpanelTrackPayload({
  anonymousId,
  clientTransport,
  eventName,
  eventProperties,
  insertId,
  superProperties,
  token,
  userId,
  userProperties,
}: T_MixpanelTrackPayloadInput): T_MixpanelTrackEvent[] {
  return [
    {
      event: eventName,
      properties: {
        ...getMixpanelEventProperties({
          clientTransport,
          eventProperties,
          superProperties,
          userId,
          userProperties,
        }),
        token,
        distinct_id: userId ?? anonymousId,
        $device_id: anonymousId,
        $insert_id: insertId,
        $user_id: userId,
      },
    },
  ];
}
